import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Input, Button, Typography, Spin, Switch, Checkbox, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { adminRestaurantPosService } from "../../services/adminService";

const { Text } = Typography;

export interface PrintCustomerDetails {
  customerName: string;
  companyName?: string;
  phone?: string;
  address?: string;
  email?: string;
  tinVatNumber?: string;
}

interface PrintReceiptModalProps {
  open: boolean;
  title?: string;
  defaults?: Partial<PrintCustomerDetails>;
  onCancel: () => void;
  onPrint: (customer?: PrintCustomerDetails) => void | Promise<void>;
}

const normalizePhone = (v?: string) => (v || "").replace(/\D/g, "");

export const PrintReceiptModal: React.FC<PrintReceiptModalProps> = ({
  open,
  title = "Print Receipt",
  defaults,
  onCancel,
  onPrint,
}) => {
  const [form] = Form.useForm();
  const [includeDetails, setIncludeDetails] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [matchedCustomer, setMatchedCustomer] = useState<any | null>(null);
  const [saveNew, setSaveNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoMatchRef = useRef(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setIncludeDetails(false);
      setMatchedCustomer(null);
      setSaveNew(false);
      setSearchResults([]);
      setSearchValue("");
      setShowResults(false);
      autoMatchRef.current = false;
      if (defaults) {
        form.setFieldsValue({
          customerName: defaults.customerName || "",
          companyName: defaults.companyName || "",
          phone: defaults.phone || "",
          address: defaults.address || "",
          email: defaults.email || "",
          tinVatNumber: defaults.tinVatNumber || "",
        });
      }
    }
    // Reset only when the modal transitions to open; ignore parent re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyCustomer = (c: any) => {
    form.setFieldsValue({
      customerName: c.full_name || c.customerName || "",
      companyName: c.company_name || c.companyName || "",
      phone: c.phone || "",
      address:
        c.address ||
        [c.address_line_1, c.address_line_2].filter(Boolean).join(", ") ||
        "",
      email: c.email || "",
      tinVatNumber: c.tin_vat_number || c.tinVatNumber || "",
    });
    setMatchedCustomer(c.id ? c : null);
    setSearchResults([]);
    setSearchValue("");
    setShowResults(false);
  };

  const runSearch = async (q: string): Promise<any[]> => {
    if (!q || q.trim().length < 2) return [];
    try {
      const result = await adminRestaurantPosService.searchCustomers(q.trim());
      const rows = result?.data || [];
      return Array.isArray(rows) ? rows : [];
    } catch {
      return [];
    }
  };

  // Auto-match saved guest profile when tax details are switched on
  const handleToggle = async (checked: boolean) => {
    setIncludeDetails(checked);
    if (!checked) return;

    const values = form.getFieldsValue();
    const query = values.phone || values.email || values.customerName || "";
    if (query.trim().length < 2 || autoMatchRef.current) return;
    autoMatchRef.current = true;

    setSearchLoading(true);
    try {
      const rows = await runSearch(query);
      const qNorm = normalizePhone(values.phone);
      const qEmail = (values.email || "").trim().toLowerCase();
      let match: any | null = null;
      if (qNorm.length >= 7) {
        match = rows.find((r) => normalizePhone(r.phone) === qNorm) || null;
      }
      if (!match && qEmail) {
        match = rows.find((r) => (r.email || "").trim().toLowerCase() === qEmail) || null;
      }
      if (match) {
        applyCustomer(match);
        message.success(`Matched saved customer: ${match.full_name}`);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced manual search on keystroke
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!includeDetails || !searchValue || searchValue.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      const rows = await runSearch(searchValue);
      setSearchResults(rows);
      setShowResults(rows.length > 0);
      setSearchLoading(false);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchValue, includeDetails]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resultsRef.current && !resultsRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    if (showResults) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showResults]);

  const handlePrint = async () => {
    if (!includeDetails) {
      setPrinting(true);
      try {
        await onPrint(undefined);
      } finally {
        setPrinting(false);
      }
      return;
    }

    try {
      await form.validateFields();
    } catch {
      return;
    }

    setPrinting(true);
    try {
      const values = form.getFieldsValue();
      if (saveNew && !matchedCustomer) {
        setSaving(true);
        try {
          await adminRestaurantPosService.createCustomer({
            full_name: values.customerName,
            company_name: values.companyName || undefined,
            phone: values.phone || undefined,
            address_line_1: values.address || undefined,
            email: values.email || undefined,
            tin_vat_number: values.tinVatNumber || undefined,
          });
          message.success("Customer saved to guest list");
        } catch {
          message.warning("Could not save customer — printing anyway");
        } finally {
          setSaving(false);
        }
      }
      await onPrint({
        customerName: values.customerName,
        companyName: values.companyName || undefined,
        phone: values.phone || undefined,
        address: values.address || undefined,
        email: values.email || undefined,
        tinVatNumber: values.tinVatNumber || undefined,
      });
    } finally {
      setPrinting(false);
    }
  };

  const showSave = includeDetails && !matchedCustomer;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      width={540}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="print" type="primary" loading={printing || saving} onClick={handlePrint}>
          {includeDetails ? "Print with Details" : "Print"}
        </Button>,
      ]}
    >
      <Spin spinning={printing || searchLoading}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            marginBottom: 14,
          }}
        >
          <div>
            <Text strong>Include tax invoice details</Text>
            <div style={{ fontSize: 12, color: "#888" }}>
              Customer name, company, address, TIN/VAT &amp; email on the receipt
            </div>
          </div>
          <Switch checked={includeDetails} onChange={handleToggle} />
        </div>

        {includeDetails && (
          <>
            <div style={{ marginBottom: 14, position: "relative" }}>
              <Text strong style={{ display: "block", marginBottom: 4 }}>
                Search Existing Customer
              </Text>
              <Input
                placeholder="Type name, phone, company or email to find a saved customer..."
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setShowResults(true);
                }}
                onFocus={() => {
                  if (searchResults.length > 0) setShowResults(true);
                }}
                allowClear
              />
              {showResults && searchResults.length > 0 && (
                <div
                  ref={resultsRef}
                  style={{
                    position: "absolute",
                    zIndex: 1000,
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    maxHeight: 200,
                    overflowY: "auto",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  {searchResults.map((c: any) => (
                    <div
                      key={c.id}
                      onClick={() => applyCustomer(c)}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                    >
                      <div>
                        <Text strong>{c.full_name}</Text>{" "}
                        {c.company_name && <Text type="secondary">({c.company_name})</Text>}
                      </div>
                      <div style={{ fontSize: 12, color: "#888" }}>
                        {c.phone || "No phone"} {c.tin_vat_number && `• TIN: ${c.tinVatNumber || c.tin_vat_number}`}{" "}
                        {c.email && `• ${c.email}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {matchedCustomer && (
                <div style={{ fontSize: 12, color: "#52c41a", marginTop: 4 }}>
                  Matched saved customer: {matchedCustomer.full_name}
                </div>
              )}
            </div>

            <Form form={form} layout="vertical" size="small">
              <Form.Item
                name="customerName"
                label="Customer Name"
                rules={[{ required: true, message: "Customer name is required" }]}
              >
                <Input placeholder="Enter customer name" />
              </Form.Item>
              <Form.Item name="companyName" label="Company Name">
                <Input placeholder="Enter company name (optional)" />
              </Form.Item>
              <Form.Item name="phone" label="Telephone">
                <Input placeholder="Enter phone number" />
              </Form.Item>
              <Form.Item name="address" label="Address">
                <Input.TextArea rows={2} placeholder="Enter address (optional)" />
              </Form.Item>
              <Form.Item name="email" label="Email">
                <Input placeholder="Enter email address (optional)" />
              </Form.Item>
              <Form.Item name="tinVatNumber" label="TIN/VAT No">
                <Input placeholder="Enter TIN/VAT number (optional)" />
              </Form.Item>
              {showSave && (
                <Form.Item style={{ marginBottom: 0 }}>
                  <Checkbox checked={saveNew} onChange={(e) => setSaveNew(e.target.checked)}>
                    Save this customer to the guest list
                  </Checkbox>
                </Form.Item>
              )}
            </Form>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default PrintReceiptModal;
